export interface BlogPost {
  slug: string
  category: string
  title: string
  excerpt: string
  date: string
  readTime: string
  gradient: string
  iconPath: string
  content: string
}

export const blogPosts: BlogPost[] = [
  {
    slug: 'wealthspace-keynote-2026',
    category: 'Event',
    title: 'Announcing the WealthSpace Keynote 2026',
    excerpt:
      'Join us for our first-ever keynote event. A live showcase of what is next for WealthSpace, featuring product reveals, adviser stories, and a vision for the future.',
    date: '4 Jan 2026',
    readTime: '4 min read',
    gradient: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #312e81 100%)',
    iconPath:
      'M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z',
    content: `
      <p><strong>Mark your calendars.</strong> On 15 March 2026, we're hosting our first-ever WealthSpace Keynote: a live event dedicated to showcasing the future of adviser technology and celebrating the community that makes it all possible.</p>

      <h2>What to Expect</h2>
      <p>The WealthSpace Keynote will be an afternoon of product reveals, adviser stories, and a vision for where we're heading next. Here's a preview of what we have planned:</p>

      <ul>
        <li><strong>Product announcements:</strong> Get an exclusive first look at new features and capabilities we've been working on</li>
        <li><strong>Adviser stories:</strong> Hear from practices that have transformed their operations with WealthSpace</li>
        <li><strong>Roadmap preview:</strong> Learn about our plans for the next 12 months and beyond</li>
        <li><strong>Q&A with founders:</strong> Ask us anything about WealthSpace, our approach, and the future</li>
      </ul>

      <h2>Why a Keynote?</h2>
      <p>WealthSpace has grown from an idea shared between advisers to a platform trusted by practices across the UK. Along the way, we've built something we're genuinely proud of, not just the technology, but the community around it.</p>

      <p>The keynote is our way of saying thank you, sharing what we've learned, and giving you a glimpse of what's coming next. It's also a chance for us to hear from you directly about what matters most.</p>

      <h2>Event Details</h2>
      <p>The event will be held in central London with options for remote attendance. More details on venue and registration will be announced soon.</p>

      <p>In the meantime, save the date: <strong>15 March 2026</strong>. We can't wait to see you there.</p>
    `,
  },
  {
    slug: 'wealthspace-v2-launch',
    category: 'Product Update',
    title: 'WealthSpace V2 is Here: 40+ Features to Transform Your Practice',
    excerpt:
      'Our biggest release yet brings a completely reimagined platform with faster workflows, smarter AI, and powerful new integrations built from adviser feedback.',
    date: '6 Jan 2026',
    readTime: '5 min read',
    gradient: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 50%, #0f766e 100%)',
    iconPath:
      'M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z',
    content: `
      <p><strong>Today we're releasing WealthSpace V2</strong>: the biggest update in our history. Over 40 new features, a completely reimagined interface, and performance improvements across the board.</p>

      <h2>What's New</h2>
      <p>V2 represents months of work informed by feedback from hundreds of advisers. Here are some highlights:</p>

      <h3>Faster Everything</h3>
      <p>We've rebuilt our core infrastructure to be dramatically faster. Pages load in milliseconds, searches return instant results, and reports generate in seconds instead of minutes.</p>

      <h3>Smarter AI</h3>
      <p>Our AI capabilities have taken a major leap forward. The new AI assistant understands context better, writes more naturally, and learns from your preferences over time.</p>

      <h3>New Integrations</h3>
      <p>V2 introduces native integrations with the platforms you already use, from back-office systems to research tools to provider portals. Data flows automatically, reducing manual entry and errors.</p>

      <h3>Redesigned Interface</h3>
      <p>Every screen has been thoughtfully redesigned to put the most important information front and centre. Less clicking, less scrolling, more doing.</p>

      <h2>Built from Your Feedback</h2>
      <p>None of this would have been possible without the advisers who took time to share their experiences, frustrations, and ideas. V2 is as much your creation as ours.</p>

      <p>We're excited to see what you build with it.</p>
    `,
  },
  {
    slug: 'money-marketing-feature',
    category: 'Company News',
    title: 'Sharing Our Story With the Adviser Community',
    excerpt:
      'We are proud to share our story with the wider adviser community. Read how WealthSpace is helping firms reclaim their time and serve more clients.',
    date: '18 Nov 2025',
    readTime: '4 min read',
    gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)',
    iconPath:
      'M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 01-2.25 2.25M16.5 7.5V18a2.25 2.25 0 002.25 2.25M16.5 7.5V4.875c0-.621-.504-1.125-1.125-1.125H4.125C3.504 3.75 3 4.254 3 4.875V18a2.25 2.25 0 002.25 2.25h13.5M6 7.5h3v3H6v-3z',
    content: `
      <p><strong>We love sharing the WealthSpace story with the wider adviser community.</strong></p>

      <p>Our story is about how WealthSpace is helping advice practices reclaim time lost to admin, automate repetitive tasks, and ultimately serve more clients without burning out.</p>

      <h2>Key Themes</h2>
      <p>When we talk about our journey, a few themes come up again and again:</p>

      <ul>
        <li>How the idea for WealthSpace emerged from real frustrations within advice practices</li>
        <li>Our approach to building software that fits adviser workflows rather than forcing advisers to adapt</li>
        <li>What practices are experiencing as they use the platform</li>
        <li>Our vision for the future of adviser technology</li>
      </ul>

      <h2>A Growing Community</h2>
      <p>Sharing our mission with advisers who might benefit from what we're building is what motivates us.</p>

      <p>If you know an adviser who's struggling with technology that doesn't quite fit their needs, we'd love the chance to show them what's possible.</p>
    `,
  },
  {
    slug: 'industry-awards-2025',
    category: 'Our Approach',
    title: 'Why We Build for Advisers, Not for Awards',
    excerpt:
      'Recognition is nice, but it is not why we do this. Here is what actually drives the way we build WealthSpace.',
    date: '22 May 2025',
    readTime: '3 min read',
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #f97316 50%, #ef4444 100%)',
    iconPath:
      'M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 01-.982-3.172M9.497 14.25a7.454 7.454 0 00.981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 007.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 002.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 012.916.52 6.003 6.003 0 01-5.395 4.972m0 0a6.726 6.726 0 01-2.749 1.35m0 0a6.772 6.772 0 01-3.044 0',
    content: `
      <p><strong>It's easy to get distracted by industry awards and recognition.</strong> We pay attention to the UK financial services technology scene, but it's not what shapes how we build.</p>

      <h2>Building for the Day-to-Day</h2>
      <p>What matters to us is whether WealthSpace helps an adviser get through their day more easily. The most innovative technology in the world means nothing if it doesn't fit how practices actually work.</p>

      <h2>Built Around Real Workflows</h2>
      <p>We're committed to building tools that genuinely help advisers do their jobs better, rather than features designed to impress from a distance.</p>

      <h2>What This Means</h2>
      <p>Awards are nice, but they're not why we do this. We're building WealthSpace because we believe advisers deserve better technology.</p>

      <p>Thank you to everyone who has supported us on this journey: the advisers who use WealthSpace daily, the team members who pour their hearts into the product, and the partners who believe in what we're building.</p>
    `,
  },
  {
    slug: 'introducing-ramble-to-action',
    category: 'Product Launch',
    title: 'Introducing Ramble to Action: AI Meeting Capture',
    excerpt:
      'We are launching our AI-powered meeting capture feature that turns conversations into structured notes, action items, and CRM updates automatically.',
    date: '15 Sep 2024',
    readTime: '4 min read',
    gradient: 'linear-gradient(135deg, #059669 0%, #047857 50%, #065f46 100%)',
    iconPath:
      'M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z',
    content: `
      <p><strong>Every adviser knows the feeling.</strong> You've just finished an excellent client meeting. The conversation flowed naturally, you covered important ground, and the client left feeling confident about their financial future.</p>

      <p>Then you sit down to write up your notes, and realise you can only remember half of what was discussed.</p>

      <h2>Introducing Ramble to Action</h2>
      <p>Today we're launching Ramble to Action, an AI-powered feature that captures your meetings and transforms them into structured outputs automatically.</p>

      <h3>How It Works</h3>
      <p>With client permission, Ramble to Action listens to your conversations and creates:</p>

      <ul>
        <li><strong>Meeting summaries:</strong> Clear, accurate records of what was discussed</li>
        <li><strong>Action items:</strong> Tasks extracted from the conversation, ready to add to your workflow</li>
        <li><strong>CRM updates:</strong> Client information captured and prepared for your back-office system</li>
        <li><strong>Follow-up drafts:</strong> Email templates based on what was agreed</li>
      </ul>

      <h2>Built for Real Conversations</h2>
      <p>Ramble to Action is trained on thousands of hours of adviser-client conversations. It understands financial terminology, recognises when decisions are being made, and knows what information matters for compliance.</p>

      <h2>Privacy First</h2>
      <p>Client conversations are sensitive. Ramble to Action processes everything in real-time without storing audio recordings. You control what gets saved and where it goes.</p>

      <p>Ready to stop losing details from your best meetings? Get in touch to try Ramble to Action.</p>
    `,
  },
  {
    slug: 'why-we-built-wealthspace',
    category: 'Our Story',
    title: 'Why We Built WealthSpace',
    excerpt:
      'The story behind WealthSpace. Built by advisers who understood the daily frustrations of running a practice and decided to do something about it.',
    date: '3 Mar 2024',
    readTime: '5 min read',
    gradient: 'linear-gradient(135deg, #1e3a5f 0%, #1e40af 50%, #3730a3 100%)',
    iconPath:
      'M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z',
    content: `
      <p><strong>WealthSpace exists because we lived the problem we are solving.</strong> Before starting this company, we spent years working in and around financial advice practices. We saw firsthand how much time gets lost to admin, how frustrating legacy software can be, and how hard it is to find technology that actually fits the way advisers work.</p>

      <h2>The Frustration</h2>
      <p>Financial advisers do some of the most important work in the professional services world. They help people plan for retirement, protect their families, and make sense of complex financial decisions. Yet so much of an adviser's day gets consumed by tasks that add little value:</p>

      <ul>
        <li>Wrestling with clunky back-office systems</li>
        <li>Writing up meeting notes hours after the conversation</li>
        <li>Manually updating client records across multiple platforms</li>
        <li>Generating reports that should take minutes but take hours</li>
      </ul>

      <p>We kept hearing the same frustration: "I became an adviser to help people, not to spend my evenings doing paperwork."</p>

      <h2>The Opportunity</h2>
      <p>We also saw how much technology had advanced in other industries. AI was transforming how businesses operated. Modern software was becoming more intuitive, more connected, more helpful. But adviser technology seemed stuck in the past.</p>

      <blockquote>
        "Why does my phone understand me better than my practice management software?"
      </blockquote>

      <p>That question kept nagging at us. The technology existed to make advisers' lives dramatically easier. Someone just needed to build it specifically for financial advice, with a deep understanding of how practices actually operate.</p>

      <h2>The Decision</h2>
      <p>We decided to be that someone. WealthSpace was founded with a simple mission: give advisers back their time so they can focus on what matters most: their clients.</p>

      <h2>Our Founding Principles</h2>
      <p>From day one, we committed to several principles:</p>

      <ul>
        <li><strong>Built by advisers:</strong> Our team includes people who have worked in advice practices. We understand the workflows, the compliance requirements, the daily realities.</li>
        <li><strong>Genuinely useful:</strong> We do not add features for marketing purposes. Everything we build has to solve a real problem that advisers actually have.</li>
        <li><strong>Respectful of your time:</strong> Our software should save time, not consume it. If something takes longer in WealthSpace than it should, that is a bug we need to fix.</li>
        <li><strong>Transparent and fair:</strong> No hidden fees, no per-user pricing that penalises growth, no lock-in contracts. We earn your business by being useful.</li>
      </ul>

      <h2>The Journey</h2>
      <p>Building WealthSpace has been a journey of constant learning. We have spent countless hours with advisers, watching how they work, listening to their frustrations, and testing our solutions with real users.</p>

      <p>Some of our best features came from conversations that started with "you know what would be really useful..." We have learned that advisers know their business better than anyone. Our job is to listen and translate those insights into software that works.</p>

      <h2>What's Next</h2>
      <p>We're just getting started. Every day brings new ideas, new challenges, and new opportunities to help advisers do their jobs better. We're grateful to everyone who has joined us on this journey so far.</p>

      <p>If you share our frustration with the status quo, we'd love to hear from you.</p>
    `,
  },
]

export function getBlogPostBySlug(slug: string): BlogPost | undefined {
  return blogPosts.find((post) => post.slug === slug)
}
